import React, { useEffect, useState, useRef } from "react"

import { WebSource, SourceStatus } from "../../../../model/types"
import NewSourceForm from "./new_source_form"
import { useNana } from "../../../hooks/useNana"
import classNames from "classnames"
import Filter, {
  FilterType,
  filterSources,
  SortOrder,
  SourceListsType,
} from "./sources_sidebar/filter"
import List from "./sources_sidebar/sources_list"
import { PlusCircleIcon } from "@heroicons/react/20/solid"

type StatusSourceCounts = {
  new: number
  queued: number
  read: number
}

const sortKey = (sortOrder: SortOrder): string => `${sortOrder.field}:${sortOrder.direction}`

const sortOrderFromKey = (key: string): SortOrder => {
  const [field, direction] = key.split(":") as [SortOrder["field"], SortOrder["direction"]]
  return { field, direction }
}

const SORT_OPTIONS: Record<SourceStatus, { label: string; sortOrder: SortOrder }[]> = {
  new: [
    { label: "Created newest", sortOrder: { field: "createdAt", direction: "desc" } },
    { label: "Created oldest", sortOrder: { field: "createdAt", direction: "asc" } },
    {
      label: "Published newest",
      sortOrder: { field: "publicationDate", direction: "desc" },
    },
    {
      label: "Published oldest",
      sortOrder: { field: "publicationDate", direction: "asc" },
    },
  ],
  queued: [
    {
      label: "Published newest",
      sortOrder: { field: "publicationDate", direction: "desc" },
    },
    {
      label: "Published oldest",
      sortOrder: { field: "publicationDate", direction: "asc" },
    },
  ],
  read: [
    { label: "Read newest", sortOrder: { field: "lastReadAt", direction: "desc" } },
    { label: "Read oldest", sortOrder: { field: "lastReadAt", direction: "asc" } },
    {
      label: "Published newest",
      sortOrder: { field: "publicationDate", direction: "desc" },
    },
    {
      label: "Published oldest",
      sortOrder: { field: "publicationDate", direction: "asc" },
    },
  ],
}

const StatusTabs = ({
  statusSelected,
  setStatusSelected,
  counts,
  filter,
  setSortOrder,
}: {
  statusSelected: SourceStatus
  setStatusSelected: (s: SourceStatus) => void
  counts: StatusSourceCounts
  filter: FilterType
  setSortOrder: (s: SortOrder) => void
}) => {
  return (
    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 md:flex-wrap">
      <div className="flex items-center justify-start text-xs md:whitespace-nowrap md:text-sm">
        <button
          onClick={() => setStatusSelected("new")}
          className={`${
            statusSelected === "new"
              ? "bg-gray-300 dark:bg-gray-700"
              : "bg-gray-200 dark:bg-gray-800"
          } py-2 px-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          New ({counts.new})
        </button>
        <button
          onClick={() => setStatusSelected("queued")}
          className={`${
            statusSelected === "queued"
              ? "bg-gray-300 dark:bg-gray-700"
              : "bg-gray-200 dark:bg-gray-800"
          } py-2 px-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          Queued ({counts.queued})
        </button>
        <button
          onClick={() => setStatusSelected("read")}
          className={`${
            statusSelected === "read"
              ? "bg-gray-300 dark:bg-gray-700"
              : "bg-gray-200 dark:bg-gray-800"
          } py-2 px-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500`}
        >
          Read ({counts.read})
        </button>
      </div>
      <div className="ml-auto py-1 pr-2 md:pl-2">
        <select
          aria-label={`Sort ${statusSelected} sources`}
          value={sortKey(filter.sortOrder[statusSelected])}
          onChange={(event) => setSortOrder(sortOrderFromKey(event.target.value))}
          className="block max-w-36 rounded-md border-0 bg-white dark:bg-gray-800 py-1 pl-2 pr-7 text-xs text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
        >
          {SORT_OPTIONS[statusSelected].map((option) => (
            <option key={sortKey(option.sortOrder)} value={sortKey(option.sortOrder)}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export const SourcesSidebarState: {
  statusTabSelected: SourceStatus
  filter: FilterType
} = {
  statusTabSelected: "new",
  filter: {
    query: "",
    goal: null,
    sortOrder: {
      new: { field: "createdAt", direction: "desc" },
      queued: { field: "publicationDate", direction: "desc" },
      read: { field: "lastReadAt", direction: "desc" },
    },
  },
}

export default function SourcesSidebar({
  openSource,
  setOpenSource,
}: {
  openSource: WebSource | null
  setOpenSource: (source: WebSource) => void
}) {
  const { model, hub, initialLoadComplete } = useNana()

  const [filter, setFilter1] = useState<FilterType>(SourcesSidebarState.filter)
  const setFilter = (filter: FilterType) => {
    setFilter1(filter)
    SourcesSidebarState.filter = filter
  }

  const [sources, setSources] = useState<SourceListsType>(
    model
      ? filterSources(model, filter)
      : {
          new: [] as WebSource[],
          queued: [] as WebSource[],
          read: [] as WebSource[],
        },
  )

  const [statusSelected, setStatusSelected1] = useState<SourceStatus>(
    SourcesSidebarState.statusTabSelected,
  )
  const setStatusSelected = (status: SourceStatus) => {
    setStatusSelected1(status)
    SourcesSidebarState.statusTabSelected = status
  }

  const setFilterAndUpdate = (newFilter: FilterType) => {
    setFilter(newFilter)
    setSources(filterSources(model, newFilter))
  }

  // When model is available, filter, only if we need to
  useEffect(() => {
    if (model.countSources() == 0) return
    setSources(filterSources(model, filter))
  }, [initialLoadComplete])
  // A handy function that means we don't have to remove and add listeners
  // every time the search state changes
  const updateFunctionRef = useRef<(() => void) | null>(null)
  updateFunctionRef.current = () => {
    setSources(filterSources(model, filter))
  }

  useEffect(() => {
    const keys = [
      hub.on(
        "command:create-web-source",
        () => updateFunctionRef.current && updateFunctionRef.current(),
      ),
      hub.on(
        "command:delete-web-source",
        () => updateFunctionRef.current && updateFunctionRef.current(),
      ),
      hub.on(
        "command:set-web-source-field",
        () => updateFunctionRef.current && updateFunctionRef.current(),
      ),
      hub.on(
        "command:set-web-source-fields",
        () => updateFunctionRef.current && updateFunctionRef.current(),
      ),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [])

  return (
    <>
      <div className="flex gap-2 p-4">
        <div className="shrink-0">
          <NewSourceForm>
            {(open: () => void) => (
              <button
                type="button"
                onClick={() => open()}
                className="flex flex-col items-center gap-x-1.5 rounded-md bg-blue-50 dark:bg-gray-800 px-2.5 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                data-testid="new-source-button"
              >
                <PlusCircleIcon className="h-6 w-6" />
                Add
              </button>
            )}
          </NewSourceForm>
        </div>
        <div className="grow min-w-0">
          <Filter filter={filter} setFilter={setFilterAndUpdate} />
        </div>
      </div>
      <div className="grow mt-4 flex flex-col">
        <StatusTabs
          statusSelected={statusSelected}
          counts={{
            new: sources.new.length,
            queued: sources.queued.length,
            read: sources.read.length,
          }}
          setStatusSelected={setStatusSelected}
          filter={filter}
          setSortOrder={(newSortOrder) => {
            const newFilter = {
              ...filter,
              sortOrder: {
                ...filter.sortOrder,
                [statusSelected]: newSortOrder,
              },
            }
            setFilter(newFilter)
            setSources(filterSources(model, newFilter))
          }}
        />
        <div
          className={classNames("grow flex flex-col bg-white dark:bg-gray-900", {
            hidden: statusSelected != "new",
          })}
        >
          {sources.new.length == 0 && sources.read.length == 0 && (
            <div className="grow flex flex-col justify-start md:mx-8 mt-4 text-sm text-gray-700 dark:text-gray-300">
              Keep track of your sources here. You can add a new source by clicking the Add button
              above.
            </div>
          )}
          <List
            sources={sources.new}
            sourceStatus={"new"}
            openSource={openSource}
            setOpenSource={setOpenSource}
          />
        </div>
        <div
          className={classNames("grow flex flex-col bg-white dark:bg-gray-900", {
            hidden: statusSelected != "queued",
          })}
        >
          <List
            sources={sources.queued}
            sourceStatus={"queued"}
            openSource={openSource}
            setOpenSource={setOpenSource}
          />
        </div>
        <div
          className={classNames("grow flex flex-col bg-white dark:bg-gray-900", {
            hidden: statusSelected != "read",
          })}
        >
          <List
            sources={sources.read}
            sourceStatus={"read"}
            openSource={openSource}
            setOpenSource={setOpenSource}
          />
        </div>
      </div>
    </>
  )
}
